import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import {
  Colors,
  Icon,
  Icons,
  LoaderSizes,
  Loader,
} from "@sellout/ui";
import Input, { InputSizes } from "@sellout/ui/build/components/Input";
import Button, { ButtonTypes } from "@sellout/ui/build/components/Button";
import { media } from "@sellout/ui/build/utils/MediaQuery";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { IEventGraphQL } from "@sellout/models/.dist/interfaces/IEvent";
import LIST_EVENTS from "@sellout/models/.dist/graphql/queries/eventsAdminCalender.query";
import LIST_ORGANIZATIONS from "@sellout/models/.dist/graphql/queries/eventCalenderOrganizations.query";
import SET_USER_ORG_CONTEXT_ID from "@sellout/models/.dist/graphql/mutations/setUserOrgContextId.mutation";
import * as Time from "@sellout/utils/.dist/time";
import EventCard, { EventCardTypes } from "../components/EventCard";
import { useSelector, useDispatch } from "react-redux";
import { BackstageState } from "../redux/store";
import * as Polished from "polished";
import { Page, PageTitle } from "../components/PageLayout";
import IOrganization from "@sellout/models/.dist/interfaces/IOrganization";
import { setToken } from "../utils/Auth";
import useNavigateToEventDetails from "../hooks/useNavigateToEventDetails.hook";
import * as AppActions from "../redux/actions/app.actions";
import { getErrorMessage } from "@sellout/ui/build/utils/ErrorUtil";
import { AppNotificationTypeEnum } from "../models/interfaces/IAppNotification";
import ScrollTable, {
  ScrollTableBody,
  ScrollTableBodyCell,
  ScrollTableBodyRow,
  ScrollTableHeader,
  ScrollTableHeaderCell,
  ScrollTableNoContent,
  ScrollTableSpace,
  ScrollTableTypeEnum,
} from "../components/ScrollableTable";


const CalendarContainer = styled.div`
  background: ${Colors.White};
  border-radius: 10px;
  border: 1px solid ${Colors.Grey5};
  overflow: visible;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid ${Colors.Grey5};
  background: ${Colors.Grey7};

  ${media.mobile`
    flex-direction: column;
    gap: 15px;
    padding: 15px;
  `};
`;

const MonthNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${Colors.Grey5};
  }
`;

const MonthTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: ${Colors.Grey1};
  margin: 0;
  min-width: 200px;
  text-align: center;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  ${media.mobile`
    flex-direction: column;
    gap: 5px;
  `};
`;

/* ── View toggle ───────────────────────────────────────────── */
const ViewToggle = styled.div`
  display: flex;
  align-items: center;
  background: ${Colors.Grey6};
  border: 1px solid ${Colors.Grey5};
  border-radius: 8px;
  overflow: hidden;
`;

const ToggleOption = styled.button<{ active: boolean }>`
  background: ${(props) => (props.active ? Colors.Orange : "transparent")};
  color: ${(props) => (props.active ? Colors.White : Colors.Grey2)};
  border: none;
  padding: 7px 16px;
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: ${(props) => (props.active ? Colors.Orange : Colors.Grey5)};
  }
`;

/* ── Search row (below header) ─────────────────────────────── */
const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-bottom: 1px solid ${Colors.Grey5};
  background: ${Colors.White};
  flex-wrap: wrap;

  ${media.mobile`
    padding: 10px 15px;
    gap: 8px;
  `};
`;

/* ── Org type-ahead ─────────────────────────────────────────── */
const OrgTypeAheadWrapper = styled.div`
  position: relative;
  width: 250px;
  flex-shrink: 0;
`;

const OrgTypeAheadBox = styled.div<{ focused: boolean }>`
  display: flex;
  align-items: center;
  height: 40px;
  border: 1px solid
    ${(props) => (props.focused ? Colors.Grey4 : Colors.Grey5)};
  border-radius: 8px;
  background: ${Colors.White};
  padding: 0 10px 0 14px;
  gap: 6px;
  transition: border-color 0.2s;
  cursor: text;
`;

const OrgTypeAheadInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 1.4rem;
  font-weight: 500;
  color: ${Colors.Grey1};
  background: transparent;
  font-family: "neue-haas-grotesk-display", sans-serif;
  min-width: 0;
  ::placeholder {
    color: ${Colors.Grey4};
  }
`;

const OrgClearBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const OrgDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  background: ${Colors.White};
  border: 1px solid ${Colors.Grey5};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
`;

const OrgDropdownItem = styled.div<{ selected: boolean }>`
  padding: 9px 14px;
  font-size: 1.3rem;
  font-weight: 500;
  color: ${(props) => (props.selected ? Colors.Orange : Colors.Grey1)};
  background: ${(props) => (props.selected ? Colors.Grey7 : Colors.White)};
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: ${Colors.Grey6};
  }
`;

const OrgNoItems = styled.div`
  padding: 9px 14px;
  font-size: 1.3rem;
  color: ${Colors.Grey3};
`;


/* ── List view ─────────────────────────────────────────────── */
const ListContainer = styled.div`
  flex: 1;
  padding: 0;
`;

const ListEventDot = styled.div<{ isDraft: boolean; needsStub?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(props) =>
    props.needsStub
      ? Colors.Green
      : props.isDraft
      ? Colors.Orange
      : Colors.Blue};
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  width: 100%;
  overflow-x: auto;

  ${media.mobile`
    font-size: 0.9rem;
    min-width: 100%;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  `};
`;

const DayHeader = styled.div`
  padding: 15px 10px;
  text-align: center;
  font-weight: 600;
  color: ${Colors.Grey2};
  background: ${Colors.Grey6};
  border-right: 1px solid ${Colors.Grey5};

  &:last-child {
    border-right: none;
  }
`;

const DayCell = styled.div<{ isCurrentMonth: boolean; isToday: boolean }>`
  min-height: 120px;
  padding: 8px;
  border-right: 1px solid ${Colors.Grey5};
  border-bottom: 1px solid ${Colors.Grey5};
  background: ${(props) =>
    props.isCurrentMonth ? Colors.White : Colors.Grey7};
  position: relative;
  overflow-x: auto;
  overflow-y: auto;

  &:last-child {
    border-right: none;
  }

  ${(props) =>
    props.isToday &&
    `
    background: ${Colors.Blue};
  `}

  ${media.mobile`
    min-height: 80px;
    padding: 4px;
  `};
`;

const DayNumber = styled.div<{ isCurrentMonth: boolean; isToday: boolean }>`
  font-weight: ${(props) => (props.isToday ? "600" : "500")};
  color: ${(props) =>
    props.isToday
      ? Colors.White
      : props.isCurrentMonth
      ? Colors.Grey1
      : Colors.Grey3};
  margin-bottom: 5px;
  font-size: 1.4rem;
`;

const EventItem = styled.div<{ isDraft: boolean; isToday?: boolean; needsStub?: boolean }>`
  background: ${(props) => props.needsStub ? Colors.Green : (props.isDraft ? Colors.Orange : Colors.Blue)};
  color: ${Colors.White};
  padding: 2px 6px;
  margin-bottom: ${(props) => (props.isToday ? '4px' : '2px')};
  border-radius: 3px;
  font-size: 1.3rem;
  cursor: pointer;
  overflow: hidden;
  border: ${(props) => (props.isToday ? `1px solid ${Colors.White}` : 'none')};

  ${media.mobile`
    font-size: 1.0rem;
    padding: 1px 4px;
  `};
`;

const EventTime = styled.span`
  font-size: 1.3rem;
  opacity: 0.9;
  ${media.mobile`
    font-size: 1.0rem;
  `};
`;

const ModalOverlay = styled.div<{ active: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${Polished.rgba(Colors.Black, 0.4)};
  display: ${(props) => (props.active ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background: ${Colors.White};
  border-radius: 10px;
  padding: 20px;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: ${Colors.Grey1};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
`;

const GoToEventButton = styled.button`
  background: ${Colors.Orange};
  color: ${Colors.White};
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 75px;
  ${media.mobile`
   margin-top: 0px;
  `};

  &:hover {
    background: ${Colors.Blue};
  }
`;
export const LoaderContainer = styled.div`
  width: 100%;
  height: calc(100% - 100px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InlineLoaderContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${Colors.White};
`;
const NoEventsContainer = styled.div`
  padding: 20px;
  text-align: center;
  color: ${Colors.Grey3};
`;

enum MonthNames {
  January = "January",
  February = "February",
  March = "March",
  April = "April",
  May = "May",
  June = "June",
  July = "July",
  August = "August",
  September = "September",
  October = "October",
  November = "November",
  December = "December",
}

enum DayNames {
  Sunday = "Sunday",
  Monday = "Monday",
  Tuesday = "Tuesday",
  Wednesday = "Wednesday",
  Thursday = "Thursday",
  Friday = "Friday",
  Saturday = "Saturday",
}

enum TimeZone {
  AmericaDenver = "America/Denver",
}

enum NoEventsMessage {
  NoEventsForOrg = "No events found for the selected organization.",
  NoEvents = "No events found.",
}

enum CalendarText {
  EventDetails = "Event Details",
  GoToEvent = "Go to Event",
  UpcomingEventName = "Upcoming Event Name",
  Multi = "multi-",
  Single = "single-",
  AllOrganizations = "All Organizations",
  EventCalendar = "Event Calendar",
  UnnamedOrganization = "Unnamed Organization",
  EventDetailsUrl = "/admin/dashboard/events/details/overview/?eventId=",
  // Search & view controls
  SearchOrgPlaceholder = "Search organization...",
  SearchEventPlaceholder = "Search by Event or Venue Name",
  SearchBtn = "Search",
  ClearBtn = "Clear",
  ListViewLabel = "List",
  CalendarViewLabel = "Calendar",
  NoOrgResults = "No results found",
}

enum ViewMode {
  Calendar = "calendar",
  List = "list",
}

enum SortOrder {
  Asc = "asc",
  Desc = "desc",
}

const OrgTypeAheadIconSize = 14;

const SortableHeaderCell = styled(ScrollTableHeaderCell)`
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover {
    color: ${Colors.Orange};
  }
`;

const SuperAdminEventCalendar: React.FC = () => {
  const orgTypeAheadRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedOrganization, setSelectedOrganization] = useState<string>("");
  const [appliedOrganization, setAppliedOrganization] = useState<string>("");
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Calendar);
  const [orgSearchQuery, setOrgSearchQuery] = useState("");
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgFocused, setOrgFocused] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [appliedSearchText, setAppliedSearchText] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<IEventGraphQL | null>(
    null
  );
  const [pendingEventId, setPendingEventId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.Asc);

  const toggleDateSort = () => {
    setSortOrder((prev) => (prev === SortOrder.Asc ? SortOrder.Desc : SortOrder.Asc));
  };
  const navigateToEventDetails = useNavigateToEventDetails();
  const appState = useSelector((state: BackstageState) => state.app);
  const { orgId } = appState;
  const currentUserOrgId = orgId;

  // Close org dropdown on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        orgTypeAheadRef.current &&
        !orgTypeAheadRef.current.contains(e.target as Node)
      ) {
        setOrgDropdownOpen(false);
        setOrgFocused(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const getMonthDateRange = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0, 23, 59, 59);

    return {
      startDate: Math.floor(startDate.getTime() / 1000),
      endDate: Math.floor(endDate.getTime() / 1000),
    };
  };

  const { startDate, endDate } = getMonthDateRange();

  //TODO Fetch events for the month and selected organization
  const { data: eventsData, loading: eventsLoading } = useQuery(LIST_EVENTS, {
    variables: {
      query: {
        startDate,
        endDate,
      },
      ...(appliedOrganization && { orgId: appliedOrganization }),
    },
    fetchPolicy: "network-only",
    onError: (error) => {
      dispatch(
        AppActions.showNotification(
          getErrorMessage(error),
          AppNotificationTypeEnum.Error
        )
      );
    },
  });

  const { data: orgsData } = useQuery(LIST_ORGANIZATIONS, {
    fetchPolicy: "cache-first",
    onError: (error) => {
      dispatch(
        AppActions.showNotification(
          getErrorMessage(error),
          AppNotificationTypeEnum.Error
        )
      );
    },
  });

  const events: IEventGraphQL[] = eventsData?.eventsAdmin || [];

  const organizations: IOrganization[] = orgsData?.organizations || [];

  const goToPreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const getCalendarDays = (): Date[] => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDayStr = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const firstDayDenver = new Date(firstDayStr + "T00:00:00");
    const startDate = new Date(firstDayDenver);
    startDate.setDate(startDate.getDate() - firstDayDenver.getDay());

    const days: Date[] = [];
    const currentDateObj = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDateObj));
      currentDateObj.setDate(currentDateObj.getDate() + 1);
    }

    return days;
  };

  const getEventsForDate = (date: Date) => {
    const filteredEvents = displayedEvents?.filter((event) => {
      //TODO Exclude cancelled events
      if (event.cancel) return false;

      if (!event.schedule?.startsAt) {
        return false;
      }

      const eventTimezone =
        event?.venue?.address?.timezone || TimeZone.AmericaDenver;

      const eventDate = new Date(event?.schedule?.startsAt * 1000);
      const eventInVenueTimezone = new Date(
        eventDate.toLocaleString("en-US", { timeZone: eventTimezone })
      );

      const calendarInVenueTimezone = new Date(
        date.toLocaleString("en-US", { timeZone: eventTimezone })
      );

      const matches =
        eventInVenueTimezone.getDate() === calendarInVenueTimezone.getDate() &&
        eventInVenueTimezone.getMonth() ===
          calendarInVenueTimezone.getMonth() &&
        eventInVenueTimezone.getFullYear() ===
          calendarInVenueTimezone.getFullYear();

      return matches;
    });

    return filteredEvents;
  };

  //TODO Get multi-day events for a specific date
  const getMultiDayEventsForDate = (date: Date) => {
    return displayedEvents.filter((event) => {
      // Exclude cancelled events
      if (event?.cancel) return false;

      if (
        !event?.isMultipleDays ||
        !event?.schedule?.startsAt ||
        !event?.schedule?.endsAt
      )
        return false;

      const eventStart = new Date(event?.schedule?.startsAt * 1000);
      const eventEnd = new Date(event?.schedule?.endsAt * 1000);

      return date >= eventStart && date <= eventEnd;
    });
  };

  const getMultiDayEventDay = (event: IEventGraphQL, date: Date) => {
    if (!event.schedule?.startsAt) return 1;
    const eventStart = new Date(event?.schedule?.startsAt * 1000);
    const startDateOnly = new Date(
      eventStart.getFullYear(),
      eventStart.getMonth(),
      eventStart.getDate()
    );
    const checkDateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );

    const diffTime = checkDateOnly.getTime() - startDateOnly.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(1, diffDays + 1);
  };

  const getPerformanceForDate = (event: IEventGraphQL, date: Date) => {
    if (!event?.performances || event?.performances?.length === 0) return null;

    const performance = event?.performances[0];
    if (!performance?.schedule) return null;

    const dayNumber = getMultiDayEventDay(event, date);
    const scheduleIndex = dayNumber - 1;

    return performance?.schedule[scheduleIndex] || performance?.schedule[0];
  };

  const handleEventClick = (event: IEventGraphQL) => {
    setSelectedEvent(event);
    setShowModal(true);
  };

  const [setUserOrgContextId] = useMutation(SET_USER_ORG_CONTEXT_ID, {
    onCompleted(data) {
      const { token } = data.setUserOrgContextId;
      setToken(token);
      if (pendingEventId) {
        window.location.href = `${CalendarText.EventDetailsUrl}${pendingEventId}`;
        setPendingEventId(null);
      }
    },
  });

  const handleGoToEvent = () => {
    if (selectedEvent && selectedEvent._id) {
      const eventId = selectedEvent._id;
      const orgId = selectedEvent.orgId;

      setShowModal(false);
      setSelectedEvent(null);

      if (orgId === currentUserOrgId) {
        navigateToEventDetails(selectedEvent?._id);
      } else {
        setPendingEventId(eventId);
        setUserOrgContextId({
          variables: {
            orgId: orgId,
          },
        });
      }
    }
  };

  const organizationOptions = [
    { value: "", text: CalendarText.AllOrganizations },
    ...organizations?.map((org) => ({
      value: org?._id,
      text: org?.orgName || CalendarText.UnnamedOrganization,
    })),
  ];

  const filteredOrgOptions = organizationOptions.filter((opt) =>
    opt.text.toLowerCase().includes(orgSearchQuery.toLowerCase())
  );

  // Client-side filter by event name / venue name
  const displayedEvents = appliedSearchText
    ? events.filter(
        (eventVenueName) =>
          eventVenueName?.name?.toLowerCase().includes(appliedSearchText.toLowerCase()) ||
          eventVenueName?.venue?.name?.toLowerCase().includes(appliedSearchText.toLowerCase())
      )
    : events;

  const handleSearch = () => {
    setAppliedOrganization(selectedOrganization);
    setAppliedSearchText(searchText);
  };

  const handleClear = () => {
    setSearchText("");
    setAppliedSearchText("");
    setSelectedOrganization("");
    setAppliedOrganization("");
    setOrgSearchQuery("");
  };

  const calendarDays: Date[] = getCalendarDays();
  const today = new Date();

  const monthNames = Object.values(MonthNames);
  const dayNames = Object.values(DayNames);


  return (
    <Page>
      <PageTitle>{CalendarText.EventCalendar}</PageTitle>

      <CalendarContainer>
        <CalendarHeader>
          <MonthNavigation>
            <NavButton onClick={goToPreviousMonth}>
              <Icon
                icon={Icons.LeftChevronRegular}
                size={16}
                color={Colors.Grey2}
              />
            </NavButton>
            <MonthTitle>
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </MonthTitle>
            <NavButton onClick={goToNextMonth}>
              <Icon
                icon={Icons.RightChevronRegular}
                size={16}
                color={Colors.Grey2}
              />
            </NavButton>
          </MonthNavigation>

          <FilterContainer>
            {/* List / Calendar toggle */}
            <ViewToggle>
              <ToggleOption
                active={viewMode === ViewMode.List}
                onClick={() => setViewMode(ViewMode.List)}
              >
                {CalendarText.ListViewLabel}
              </ToggleOption>
              <ToggleOption
                active={viewMode === ViewMode.Calendar}
                onClick={() => setViewMode(ViewMode.Calendar)}
              >
                {CalendarText.CalendarViewLabel}
              </ToggleOption>
            </ViewToggle>
          </FilterContainer>
        </CalendarHeader>

        {/* Search row */}
        <SearchRow>
          {/* Org type-ahead */}
          <OrgTypeAheadWrapper ref={orgTypeAheadRef}>
            <OrgTypeAheadBox focused={orgFocused}>
              <Icon icon={Icons.SearchLight} size={OrgTypeAheadIconSize} color={Colors.Grey4} />
              <OrgTypeAheadInput
                placeholder={CalendarText.SearchOrgPlaceholder}
                value={
                  orgDropdownOpen
                    ? orgSearchQuery
                    : selectedOrganization
                    ? organizationOptions.find(
                        (o) => o.value === selectedOrganization
                      )?.text || ""
                    : orgSearchQuery
                }
                onChange={(e) => {
                  setOrgSearchQuery(e.target.value);
                  setOrgDropdownOpen(true);
                }}
                onFocus={() => {
                  setOrgFocused(true);
                  setOrgDropdownOpen(true);
                  // show full search query when opening
                  if (selectedOrganization) setOrgSearchQuery("");
                }}
              />
              {(selectedOrganization || orgSearchQuery) && (
                <OrgClearBtn
                  type="button"
                  onClick={() => {
                    setSelectedOrganization("");
                    setOrgSearchQuery("");
                    setOrgDropdownOpen(false);
                  }}
                >
                  <Icon icon={Icons.CancelCircle} size={OrgTypeAheadIconSize} color={Colors.Grey4} />
                </OrgClearBtn>
              )}
            </OrgTypeAheadBox>

            {orgDropdownOpen && (
              <OrgDropdown>
                {filteredOrgOptions.length === 0 ? (
                  <OrgNoItems>{CalendarText.NoOrgResults}</OrgNoItems>
                ) : (
                  filteredOrgOptions.map((opt) => (
                    <OrgDropdownItem
                      key={opt.value}
                      selected={opt.value === selectedOrganization}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setSelectedOrganization(opt.value ?? "");
                        setOrgSearchQuery("");
                        setOrgDropdownOpen(false);
                        setOrgFocused(false);
                      }}
                    >
                      {opt.text}
                    </OrgDropdownItem>
                  ))
                )}
              </OrgDropdown>
            )}
          </OrgTypeAheadWrapper>

          {/* Event / Venue name search */}
          <Input
            value={searchText}
            placeholder={CalendarText.SearchEventPlaceholder}
            size={InputSizes.Regular}
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              setSearchText(e.currentTarget.value)
            }
            onClear={() => { setSearchText(""); setAppliedSearchText(""); }}
            onEnter={handleSearch}
            width="280px"
          />

          <Button
            type={ButtonTypes.Thin}
            text={CalendarText.SearchBtn}
            onClick={handleSearch}
          />
          <Button
            type={ButtonTypes.Thin}
            text={CalendarText.ClearBtn}
            onClick={handleClear}
          />
        </SearchRow>

        {eventsLoading ? (
          <InlineLoaderContainer>
            <Loader size={LoaderSizes.Large} color={Colors.Orange} />
          </InlineLoaderContainer>
        ) : viewMode === ViewMode.Calendar ? (
          <CalendarGrid>
            {dayNames?.map((day) => (
              <DayHeader key={day}>{day}</DayHeader>
            ))}

            {calendarDays?.map((date, index) => {
              const isCurrentMonth =
                date.getMonth() === currentDate.getMonth();
              const isToday = date.toDateString() === today.toDateString();
              const dayEvents = getEventsForDate(date);
              const multiDayEvents = getMultiDayEventsForDate(date);

              return (
                <DayCell
                  key={index}
                  isCurrentMonth={isCurrentMonth}
                  isToday={isToday}
                >
                  <DayNumber isCurrentMonth={isCurrentMonth} isToday={isToday}>
                    {date.getDate()}
                  </DayNumber>

                  {/* Single day events */}
                  {dayEvents?.map((event) => (
                    <EventItem
                      key={`${CalendarText.Single}${event?._id}`}
                      isDraft={!event?.published && !event?.cancel}
                      needsStub={
                        event?.published &&
                        !event?.cancel &&
                        (!event?.stub || event?.stub === "")
                      }
                      isToday={isToday}
                      onClick={() => handleEventClick(event)}
                    >
                      <EventTime>
                        {event?.schedule?.startsAt
                          ? Time.format(
                              event.schedule.startsAt,
                              "ddd, MMM Do",
                              event.venue?.address?.timezone ||
                                TimeZone.AmericaDenver
                            )
                          : ""}
                      </EventTime>{" "}
                      {!event?.published && !event?.cancel
                        ? `(${event?.name?.trim() || CalendarText.UpcomingEventName})`
                        : event?.name}
                    </EventItem>
                  ))}

                  {/* Multi-day events */}
                  {multiDayEvents?.map((event) => {
                    const dayNumber = getMultiDayEventDay(event, date);
                    const performanceSchedule = getPerformanceForDate(
                      event,
                      date
                    );
                    return (
                      <EventItem
                        key={`${CalendarText.Multi}${event?._id}-${dayNumber}`}
                        isDraft={!event?.published && !event?.cancel}
                        needsStub={
                          event?.published &&
                          !event?.cancel &&
                          (!event?.stub || event?.stub === "")
                        }
                        isToday={isToday}
                        onClick={() => handleEventClick(event)}
                      >
                        <EventTime>
                          {performanceSchedule?.startsAt
                            ? Time.format(
                                performanceSchedule.startsAt,
                                "ddd, MMM Do",
                                event?.venue?.address?.timezone ||
                                  TimeZone.AmericaDenver
                              )
                            : Time.format(
                                date.getTime(),
                                "ddd, MMM Do",
                                event?.venue?.address?.timezone ||
                                  TimeZone.AmericaDenver
                              )}
                        </EventTime>{" "}
                        {!event?.published && !event.cancel
                          ? `(${event?.name?.trim() || CalendarText.UpcomingEventName})`
                          : event?.name}{" "}
                        (Day {dayNumber})
                      </EventItem>
                    );
                  })}
                </DayCell>
              );
            })}
          </CalendarGrid>
        ) : (
          /* List view */
          <ListContainer>
            <ScrollTable>
              <ScrollTableHeader type={ScrollTableTypeEnum.Small}>
                <ScrollTableHeaderCell type={ScrollTableTypeEnum.Small} width="16px"></ScrollTableHeaderCell>
                <ScrollTableHeaderCell type={ScrollTableTypeEnum.Small} flex="1">Event Name</ScrollTableHeaderCell>
                <ScrollTableSpace />
                <ScrollTableHeaderCell type={ScrollTableTypeEnum.Small} flex="1">Organization</ScrollTableHeaderCell>
                <ScrollTableSpace />
                <ScrollTableHeaderCell type={ScrollTableTypeEnum.Small} flex="1">Venue</ScrollTableHeaderCell>
                <ScrollTableSpace />
                <SortableHeaderCell
                  type={ScrollTableTypeEnum.Small}
                  flex="1"
                  onClick={toggleDateSort}
                >
                  <span>Date/Time</span>
                  <Icon
                    icon={Icons.Sort}
                    size={12}
                    color={Colors.Orange}
                    margin="0 0 0 6px"
                  />
                </SortableHeaderCell>
              </ScrollTableHeader>
              <ScrollTableBody type={ScrollTableTypeEnum.Small}>
                {displayedEvents.length === 0 ? (
                  <ScrollTableNoContent>
                    {appliedOrganization
                      ? NoEventsMessage.NoEventsForOrg
                      : NoEventsMessage.NoEvents}
                  </ScrollTableNoContent>
                ) : (
                  [...displayedEvents]
                    .filter((event) => !event?.cancel && event?.schedule?.startsAt)
                    .sort((a, b) => {
                      const timeA = a.schedule?.startsAt ?? 0;
                      const timeB = b.schedule?.startsAt ?? 0;
                      return sortOrder === SortOrder.Asc ? timeA - timeB : timeB - timeA;
                    })
                    .map((event) => {
                      const isDraft = !event.published && !event.cancel;
                      const needsStub =
                        event.published &&
                        !event.cancel &&
                        (!event.stub || event.stub === "");
                      const timezone =
                        event?.venue?.address?.timezone || TimeZone.AmericaDenver;
                      const dateText =
                        event.isMultipleDays && event.schedule?.endsAt
                          ? `${Time.format(
                              event?.schedule.startsAt,
                              "ddd, MMM Do",
                              timezone
                            )} – ${Time.format(
                              event?.schedule.endsAt,
                              "ddd, MMM Do",
                              timezone
                            )}`
                          : event.schedule?.startsAt
                          ? Time.format(
                              event?.schedule.startsAt,
                              "ddd, MMM Do, h:mma",
                              timezone
                            )
                          : "";
                      const eventOrg = organizations.find((org) => org._id === event.orgId);
                      const orgName = eventOrg?.orgName || (event as any).org?.orgName || "—";
                      return (
                        <ScrollTableBodyRow
                          key={event._id}
                          height="44px"
                          padding="0 16px"
                          onClick={() => handleEventClick(event)}
                        >
                          <ScrollTableBodyCell width="16px">
                            <ListEventDot isDraft={isDraft} needsStub={needsStub} />
                          </ScrollTableBodyCell>
                          <ScrollTableBodyCell flex="1">
                            {isDraft
                              ? `(${event.name?.trim() || CalendarText.UpcomingEventName})`
                              : event.name}
                          </ScrollTableBodyCell>
                          <ScrollTableSpace />
                          <ScrollTableBodyCell flex="1">
                            {orgName}
                          </ScrollTableBodyCell>
                          <ScrollTableSpace />
                          <ScrollTableBodyCell flex="1">
                            {event.venue?.name || "—"}
                          </ScrollTableBodyCell>
                          <ScrollTableSpace />
                          <ScrollTableBodyCell flex="1">
                            {dateText}
                          </ScrollTableBodyCell>
                        </ScrollTableBodyRow>
                      );
                    })
                )}
              </ScrollTableBody>
            </ScrollTable>
          </ListContainer>
        )}
      </CalendarContainer>

      {viewMode === ViewMode.Calendar && displayedEvents.length === 0 && !eventsLoading && (
        <NoEventsContainer>
          {appliedOrganization
            ? NoEventsMessage.NoEventsForOrg
            : NoEventsMessage.NoEvents}
        </NoEventsContainer>
      )}

      <ModalOverlay
        active={showModal}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowModal(false);
            setSelectedEvent(null);
          }
        }}
      >
        {selectedEvent ? (
          <ModalContent>
            <ModalHeader>
              <ModalTitle>{CalendarText.EventDetails}</ModalTitle>
              <CloseButton
                onClick={() => {
                  setShowModal(false);
                  setSelectedEvent(null);
                }}
              >
                <Icon icon={Icons.Cancel} size={16} color={Colors.Grey2} />
              </CloseButton>
            </ModalHeader>

            <EventCard
              event={selectedEvent}
              footer={true}
              type={EventCardTypes.Modal}
            />

            <GoToEventButton onClick={handleGoToEvent}>
              {CalendarText.GoToEvent}
            </GoToEventButton>
          </ModalContent>
        ):""}
      </ModalOverlay>
    </Page>
  );
};

export default SuperAdminEventCalendar;